import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Loader,
  Plus,
  X,
  ExternalLink,
  Receipt,
  ChevronRight,
} from "lucide-react";
import { QuoteSelector } from "@/components/QuoteSelector";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { QuoteEditorController } from "@/modules/quotes/QuoteEditorController";
import { toast } from "sonner";
import {
  QuoteResponse,
  QUOTE_STATUS_DISPLAY_NAMES,
  QuoteStatus,
} from "@forgepoint/shared";
import { formatConvertedAmount } from "@/lib/quoteCurrency";
import { cn } from "@/lib/utils";

interface OpportunityQuotesSectionProps {
  opportunityId: string;
  isEditing?: boolean;
  compact?: boolean;
  readOnly?: boolean;
}

/**
 * Displays linked quotes for an opportunity with ability to link new quotes.
 * Shows a list of quotes with their details (number, revision, name, status, total).
 * Each quote opens the existing quote editor surface.
 * The section is collapsible, matching the tasks/threads section pattern.
 * Hidden when the opportunity is in edit mode.
 *
 * @param opportunityId - The ID of the opportunity
 * @param isEditing - Whether the opportunity is in edit mode. When true, the component is hidden.
 * @param readOnly - When true, hides the link-quote affordance.
 */
export const OpportunityQuotesSection: React.FC<
  OpportunityQuotesSectionProps
> = ({
  opportunityId,
  isEditing = false,
  compact = false,
  readOnly = false,
}) => {
  const { quotes, fetchQuotes, updateQuote, listLoading } =
    useRecordQuotesStore();

  const [linkedQuoteIds, setLinkedQuoteIds] = useState<string[]>([]);
  const [showQuoteSelector, setShowQuoteSelector] = useState(false);
  const [selectedQuoteIdToLink, setSelectedQuoteIdToLink] = useState<
    string | null
  >(null);
  const [isLinking, setIsLinking] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [editingQuoteId, setEditingQuoteId] = useState<string | undefined>();

  useEffect(() => {
    fetchQuotes({ opportunityId });
  }, [opportunityId, fetchQuotes]);

  useEffect(() => {
    const linkedQuotes = Array.from(quotes.values()).filter(
      (quote) => quote.opportunityId === opportunityId,
    );
    setLinkedQuoteIds(linkedQuotes.map((q) => q.id));
  }, [quotes, opportunityId]);

  if (isEditing) {
    return null;
  }

  const linkedQuotes = linkedQuoteIds
    .map((id) => quotes.get(id))
    .filter((q): q is QuoteResponse => q !== undefined);

  /**
   * Links a selected quote to this opportunity.
   */
  const handleLinkQuote = async () => {
    if (!selectedQuoteIdToLink) {
      return;
    }

    setIsLinking(true);
    try {
      const result = await updateQuote(selectedQuoteIdToLink, {
        opportunityId,
      });

      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success("Quote linked to opportunity");
        setShowQuoteSelector(false);
        setSelectedQuoteIdToLink(null);
        await fetchQuotes({ opportunityId });
      }
    } catch {
      toast.error("Failed to link quote");
    } finally {
      setIsLinking(false);
    }
  };

  /**
   * Formats the total for a quote as a currency string in the quote's display
   * currency, honoring the org's configured rounding decimals.
   * @param quote - The quote to format total for
   * @returns Formatted currency string or empty string if no total
   */
  const formatQuoteTotal = (quote: QuoteResponse) => {
    if (!quote.total) {
      return "";
    }
    return formatConvertedAmount(quote.total, quote);
  };

  const refreshOpportunityQuotes = async () => {
    await fetchQuotes({ opportunityId });
  };

  const handleQuoteSaved = async () => {
    await refreshOpportunityQuotes();
    setEditingQuoteId(undefined);
  };

  const handleQuoteArchived = async () => {
    await refreshOpportunityQuotes();
  };

  return (
    <>
      <div className={compact ? "" : "border-t pt-6"}>
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div
            className={cn(
              "flex items-center justify-between",
              compact ? "mb-3" : "mb-4",
            )}
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 group cursor-pointer"
              >
                <ChevronRight
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform duration-200",
                    isOpen && "rotate-90",
                  )}
                />
                <h3
                  className={cn(
                    "font-semibold",
                    compact ? "text-sm" : "text-lg",
                  )}
                >
                  Quotes
                </h3>
                {!listLoading && linkedQuotes.length > 0 && (
                  <Badge variant="secondary" className="text-xs tabular-nums">
                    {linkedQuotes.length}
                  </Badge>
                )}
              </button>
            </CollapsibleTrigger>
            {!showQuoteSelector && !readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowQuoteSelector(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Link Quote
              </Button>
            )}
          </div>

          <CollapsibleContent>
            {showQuoteSelector && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/50 mb-4 animate-in fade-in slide-in-from-top-1 duration-150">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Select a quote to link:</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowQuoteSelector(false);
                      setSelectedQuoteIdToLink(null);
                    }}
                    disabled={isLinking}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <QuoteSelector
                  currentlySelectedId={selectedQuoteIdToLink}
                  onSelectId={setSelectedQuoteIdToLink}
                  placeholder="Select a quote..."
                  disabled={isLinking}
                  unlinkedOnly
                />
                <Button
                  onClick={handleLinkQuote}
                  disabled={!selectedQuoteIdToLink || isLinking}
                  className="w-full"
                >
                  {isLinking && (
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Link Quote
                </Button>
              </div>
            )}

            {listLoading && linkedQuotes.length === 0 ? (
              <div className="space-y-1.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3.5 w-1/3" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <Skeleton className="h-5 w-14 rounded-full" />
                      <Skeleton className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : linkedQuotes.length === 0 ? (
              <div
                className={cn(
                  "flex flex-col items-center justify-center px-4 border rounded-lg text-center",
                  compact ? "py-6" : "py-8",
                )}
              >
                <Receipt className="h-7 w-7 text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No quotes linked yet
                </p>
                <p className="text-xs text-muted-foreground/60 mt-0.5">
                  Link a quote to track it alongside this opportunity
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {linkedQuotes.map((quote) => {
                  const total = formatQuoteTotal(quote);
                  const hasSecondary = quote.name || total;

                  return (
                    <div
                      key={quote.id}
                      className="w-full flex items-stretch justify-between border rounded-lg hover:bg-muted/50 transition-colors animate-in fade-in slide-in-from-bottom-1 duration-200 group"
                    >
                      <button
                        type="button"
                        onClick={() => setEditingQuoteId(quote.id)}
                        className="flex flex-1 min-w-0 items-center justify-between p-3 text-left"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">
                            <span>#{quote.quoteDisplayNumber}</span>
                            {quote.revision > 0 && (
                              <span className="text-muted-foreground font-normal ml-1.5">
                                Rev {quote.revision}
                              </span>
                            )}
                          </p>
                          {hasSecondary && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {quote.name && (
                                <span className="text-xs text-muted-foreground">
                                  {quote.name}
                                </span>
                              )}
                              {quote.name && total && (
                                <span className="text-xs text-muted-foreground/50">
                                  &middot;
                                </span>
                              )}
                              {total && (
                                <span className="text-xs text-muted-foreground">
                                  {total}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0 ml-2">
                          <QuoteStatusBadge quote={quote} />
                        </div>
                      </button>
                      <div className="flex items-center shrink-0 p-3 pl-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground/60 hover:text-foreground"
                              asChild
                            >
                              <a
                                href={`/public/quotes/${quote.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open public quote #${quote.quoteDisplayNumber}`}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Open public quote</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>

      <Sheet
        open={!!editingQuoteId}
        onOpenChange={(open) => !open && setEditingQuoteId(undefined)}
      >
        <SheetContent className="w-full sm:max-w-4xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle>Edit Quote</SheetTitle>
          </SheetHeader>
          <div className="flex-1 relative overflow-hidden">
            {editingQuoteId && (
              <QuoteEditorController
                quoteId={editingQuoteId}
                onSaved={handleQuoteSaved}
                onCancel={() => setEditingQuoteId(undefined)}
                onArchived={handleQuoteArchived}
                onNavigateToQuote={setEditingQuoteId}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

/**
 * Returns tailwind classes for a quote status badge matching the QuotesController palette.
 * @param status - The quote status
 * @param isArchived - Whether the quote is archived
 * @returns Tailwind class string for the badge
 */
function getQuoteStatusClasses(
  status: QuoteStatus,
  isArchived: boolean,
): string {
  if (isArchived) {
    return "bg-warning/10 text-warning-foreground border-warning/30";
  }
  if (status === "FINALIZED") {
    return "bg-success/10 text-success-foreground border-success/30";
  }
  return "bg-muted text-muted-foreground border-border";
}

/**
 * Renders a small status badge for a quote, using the same color palette as the quotes table.
 * @param quote - The quote to render the badge for
 * @returns A badge element indicating the quote status
 */
function QuoteStatusBadge({ quote }: { quote: QuoteResponse }) {
  const isArchived =
    quote.archivedAt !== null && quote.archivedAt !== undefined;
  const displayStatus = isArchived
    ? "Archived"
    : QUOTE_STATUS_DISPLAY_NAMES[quote.status];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border",
        getQuoteStatusClasses(quote.status, isArchived),
      )}
    >
      {displayStatus}
    </span>
  );
}
